// CardCraft.js - Component that renders compositions as pure React
import React, { useState, useEffect } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import { FormControl, FormLabel, TextField } from "@mui/material";
import { getQwantum } from 'qwanyx';

// Pure renderer - converts serialized CraftJS data to React components
const renderFromSerialized = (serializedData, resolvers, nodeId = 'ROOT') => {
  if (!serializedData || !nodeId || !serializedData[nodeId]) {
    return null;
  }

  const node = serializedData[nodeId];
  
  // Handle both data structure formats:
  // Format 1: {type: {resolvedName: "Component"}, nodes: [...], props: {...}}
  // Format 2: {data: {type: {resolvedName: "Component"}, nodes: [...], props: {...}}}
  const nodeData = node.data || node;
  
  // Get component type name
  const componentType = nodeData.type?.resolvedName || nodeData.type;
  
  // Get the component from resolvers
  const Component = resolvers[componentType];
  if (!Component) {
    console.warn(`Component ${componentType} not found in resolvers`);
    return null;
  }

  // Get props, excluding CraftJS-specific ones
  const props = { ...nodeData.props };
  
  // Remove CraftJS-specific props that shouldn't be passed to pure components
  delete props.canvas;
  delete props.custom;
  delete props.hidden; // Handle hidden separately if needed
  
  // Handle hidden state
  if (nodeData.hidden) {
    return null;
  }

  // Recursively render children
  const children = nodeData.nodes?.map(childId => 
    renderFromSerialized(serializedData, resolvers, childId)
  ).filter(Boolean) || [];

  try {
    // For canvas components, render children inside
    if (nodeData.isCanvas && children.length > 0) {
      return React.createElement(Component, props, ...children);
    }
    
    // For leaf components or components without children
    return React.createElement(Component, props, children.length > 0 ? children : undefined);
  } catch (error) {
    console.error(`Error rendering component ${componentType}:`, error);
    return React.createElement('div', 
      { style: { color: 'red', padding: '8px', border: '1px solid red' } },
      `Error rendering ${componentType}`
    );
  }
};

export const CardCraft = ({ 
  name = "CardCraft",
  documentId = "",
  qwantumId = "card",
  width = "100%",
  height = "auto",
  hidden = false,
  style = {},
  editable = true, // NEW: control CraftJS behavior
  resolvers = null, // NEW: pass resolvers for non-editable mode
  ...props 
}) => {
  const {
    connectors: { connect, drag },
    isSelected,
    isHovered,
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled, resolver } = useEditor((state) => ({
    enabled: state.options.enabled,
    resolver: state.options.resolver
  }));

  const [serializedData, setSerializedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadComposition = async () => {
      if (!documentId) {
        setSerializedData(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await getQwantum(documentId, qwantumId);
        if (data) {
          console.log('Fetched data structure:', data);
          console.log('Data type:', typeof data);
          
          // Parse JSON string if needed
          const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
          console.log('Parsed data:', parsedData);
          console.log('ROOT node:', parsedData['ROOT']);
          
          setSerializedData(parsedData);
        } else {
          setError(`No qwantum data found for ${qwantumId}`);
        }
      } catch (err) {
        console.error('Failed to load composition:', err);
        setError(err.message || 'Failed to load composition');
      } finally {
        setLoading(false);
      }
    };

    loadComposition();
  }, [documentId, qwantumId]);

  // Return early if hidden
  if (hidden) return null;

  // Selection styling for editor
  const getSelectionStyle = () => {
    if (!enabled || !editable) return {};

    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const containerStyle = {
    width,
    height,
    position: 'relative',
    ...style,
    ...getSelectionStyle()
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          border: '1px dashed #ddd'
        }}>
          Loading composition...
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#f44336',
          backgroundColor: '#ffebee',
          border: '1px solid #f44336'
        }}>
          Error: {error}
        </div>
      );
    }

    if (!documentId) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          border: '1px dashed #ddd'
        }}>
          No document ID specified
        </div>
      );
    }

    if (!serializedData) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          border: '1px dashed #ddd'
        }}>
          No composition data
        </div>
      );
    }

    // Render the composition using pure React
    try {
      const rootNode = serializedData['ROOT'];
      console.log('ROOT node data:', rootNode);
      
      // Handle both data structure formats
      const nodeData = rootNode.data || rootNode;
      
      if (!nodeData || !nodeData.nodes || nodeData.nodes.length === 0) {
        console.log('Empty composition - nodeData:', nodeData);
        return (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#666',
            backgroundColor: '#f5f5f5',
            border: '1px dashed #ddd'
          }}>
            Empty composition
          </div>
        );
      }

      console.log('Rendering children:', nodeData.nodes);
      // Render all root children - always try Editor resolver first, fallback to prop
      const activeResolver = resolver || resolvers;
      return nodeData.nodes.map(childId => 
        renderFromSerialized(serializedData, activeResolver, childId)
      );
    } catch (err) {
      console.error('Error rendering composition:', err);
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#f44336',
          backgroundColor: '#ffebee',
          border: '1px solid #f44336'
        }}>
          Error rendering composition: {err.message}
        </div>
      );
    }
  };

  return (
    <div
      ref={ref => editable && enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={containerStyle}
      {...props}
    >
      {renderContent()}
    </div>
  );
};

const CardCraftSettings = () => {
  const {
    actions: { setProp },
    props
  } = useNode((node) => ({
    props: node.data.props
  }));

  const [localDocumentId, setLocalDocumentId] = useState(props.documentId || '');
  const [localQwantumId, setLocalQwantumId] = useState(props.qwantumId || 'card');
  const [localWidth, setLocalWidth] = useState(props.width || '100%');
  const [localHeight, setLocalHeight] = useState(props.height || 'auto');

  useEffect(() => setLocalDocumentId(props.documentId || ''), [props.documentId]);
  useEffect(() => setLocalQwantumId(props.qwantumId || 'card'), [props.qwantumId]);
  useEffect(() => setLocalWidth(props.width || '100%'), [props.width]);
  useEffect(() => setLocalHeight(props.height || 'auto'), [props.height]);

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={props.name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="CardCraft"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Document ID (_id)</FormLabel>
        <TextField
          value={localDocumentId}
          onChange={(e) => setLocalDocumentId(e.target.value)}
          onBlur={(e) => setProp(props => props.documentId = e.target.value)}
          size="small"
          placeholder="Enter document _id"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Qwantum ID</FormLabel>
        <TextField
          value={localQwantumId}
          onChange={(e) => setLocalQwantumId(e.target.value)}
          onBlur={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="card"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Width</FormLabel>
        <TextField
          value={localWidth}
          onChange={(e) => setLocalWidth(e.target.value)}
          onBlur={(e) => setProp(props => props.width = e.target.value)}
          size="small"
          placeholder="100%, 500px, auto"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Height</FormLabel>
        <TextField
          value={localHeight}
          onChange={(e) => setLocalHeight(e.target.value)}
          onBlur={(e) => setProp(props => props.height = e.target.value)}
          size="small"
          placeholder="auto, 400px, 100vh"
        />
      </FormControl>
    </div>
  );
};

CardCraft.craft = {
  props: {
    name: "CardCraft",
    documentId: "",
    qwantumId: "card",
    width: "100%",
    height: "auto",
    editable: true
  },
  related: {
    settings: CardCraftSettings
  }
};